// ════════════════════════════════════════════════════════════════════════════
// apiErrors — traduz códigos de erro PostgREST/Supabase para PT-BR amigável
//
// Tabela baseada em codes oficiais:
// https://postgrest.org/en/stable/references/errors.html
// https://supabase.com/docs/guides/troubleshooting/postgresql-error-codes
// ════════════════════════════════════════════════════════════════════════════

interface ApiErrorLike {
  code?: string;
  message?: string;
  details?: string;
  hint?: string;
  status?: number;
}

interface FriendlyError {
  title: string;
  description: string;
  /** Sugestão de ação para o usuário */
  action?: string;
  /** Severidade — para escolher cor da UI */
  severity: "info" | "warning" | "danger";
}

/**
 * Mapeamento exaustivo dos códigos PostgREST + Supabase Auth + custom LPA.
 * Lookup case-insensitive.
 */
const POSTGREST_CODES: Record<string, FriendlyError> = {
  // Resource not found / RLS bloqueando
  PGRST116: {
    title: "Registro não encontrado",
    description: "O registro que você procura não existe ou você não tem permissão para vê-lo.",
    action: "Volte para a lista e tente novamente. Se o problema persistir, contate seu consultor.",
    severity: "warning",
  },
  PGRST301: {
    title: "Sessão expirada",
    description: "Sua sessão expirou por segurança.",
    action: "Faça login novamente para continuar.",
    severity: "warning",
  },
  // Conflict / unique constraint
  "23505": {
    title: "Registro duplicado",
    description: "Já existe um registro com esses dados.",
    action: "Use um valor diferente ou edite o registro existente.",
    severity: "warning",
  },
  // FK violation
  "23503": {
    title: "Operação não permitida",
    description:
      "Este registro está vinculado a outros dados e não pode ser removido sem antes desfazer essas relações.",
    severity: "warning",
  },
  // Not null violation
  "23502": {
    title: "Campo obrigatório vazio",
    description: "Um campo obrigatório não foi preenchido.",
    action: "Verifique todos os campos marcados com * antes de salvar.",
    severity: "warning",
  },
  // Check constraint
  "23514": {
    title: "Valor inválido",
    description: "O valor informado não atende às regras do sistema.",
    action: "Revise os campos preenchidos.",
    severity: "warning",
  },
  // Permission denied
  "42501": {
    title: "Acesso negado",
    description: "Você não tem permissão para executar esta ação.",
    action: "Entre em contato com o administrador se acredita ser um engano.",
    severity: "danger",
  },
  // Network / timeout
  PGRST002: {
    title: "Tempo esgotado",
    description: "O servidor demorou demais para responder.",
    action: "Verifique sua conexão e tente novamente.",
    severity: "warning",
  },
};

/** Códigos de erro do Supabase Auth */
const AUTH_CODES: Record<string, FriendlyError> = {
  invalid_credentials: {
    title: "E-mail ou senha incorretos",
    description: "Não conseguimos validar suas credenciais.",
    action: 'Verifique e-mail/senha. Se esqueceu, use "Recuperar senha".',
    severity: "warning",
  },
  email_not_confirmed: {
    title: "E-mail não confirmado",
    description: "Você precisa confirmar seu e-mail antes de fazer login.",
    action: "Verifique sua caixa de entrada (incluindo spam) e clique no link.",
    severity: "warning",
  },
  user_already_exists: {
    title: "E-mail já cadastrado",
    description: "Este e-mail já tem uma conta.",
    action: "Faça login ou recupere a senha.",
    severity: "info",
  },
  weak_password: {
    title: "Senha muito fraca",
    description: "Sua senha precisa ser mais forte.",
    action: "Use ao menos 8 caracteres, com letras maiúsculas, minúsculas, números e símbolos.",
    severity: "warning",
  },
  rate_limit_exceeded: {
    title: "Muitas tentativas",
    description: "Você excedeu o limite de tentativas.",
    action: "Aguarde alguns minutos antes de tentar novamente.",
    severity: "warning",
  },
};

/** Códigos HTTP genéricos (status numérico) */
const HTTP_CODES: Record<number, FriendlyError> = {
  401: {
    title: "Sessão expirada",
    description: "Sua sessão expirou. Faça login novamente.",
    severity: "warning",
  },
  403: {
    title: "Acesso negado",
    description: "Você não tem permissão para esta ação.",
    severity: "danger",
  },
  404: {
    title: "Não encontrado",
    description: "O recurso solicitado não existe.",
    severity: "warning",
  },
  429: {
    title: "Muitas requisições",
    description: "Você fez muitas operações em sequência.",
    action: "Aguarde alguns segundos antes de tentar novamente.",
    severity: "warning",
  },
  500: {
    title: "Erro do servidor",
    description: "Algo deu errado no servidor.",
    action: "Tente novamente em alguns minutos. Se persistir, contate o consultor.",
    severity: "danger",
  },
  502: {
    title: "Servidor indisponível",
    description: "Não conseguimos conectar ao servidor.",
    action: "Aguarde alguns minutos e tente novamente.",
    severity: "danger",
  },
  503: {
    title: "Serviço em manutenção",
    description: "O sistema está temporariamente indisponível.",
    action: "Aguarde alguns minutos.",
    severity: "warning",
  },
};

/** Fallback genérico amigável */
const GENERIC_FALLBACK: FriendlyError = {
  title: "Algo deu errado",
  description: "Não conseguimos completar sua ação.",
  action: "Tente novamente. Se persistir, contate seu consultor com a mensagem técnica abaixo.",
  severity: "danger",
};

/**
 * Traduz erro técnico → mensagem PT-BR amigável.
 *
 * @param err — error from Supabase / fetch / unknown
 * @param fallbackTitle — título override se nenhum mapeamento bater
 */
export function friendlyError(err: unknown, fallbackTitle?: string): FriendlyError {
  if (!err) return { ...GENERIC_FALLBACK, title: fallbackTitle ?? GENERIC_FALLBACK.title };

  const e = err as ApiErrorLike;

  // PostgREST code
  if (e.code && POSTGREST_CODES[e.code]) {
    return POSTGREST_CODES[e.code];
  }

  // Auth code (em e.message ou e.code)
  const authKey = (e.code ?? e.message ?? "").toLowerCase();
  for (const [k, v] of Object.entries(AUTH_CODES)) {
    if (authKey.includes(k)) return v;
  }

  // HTTP status
  if (e.status && HTTP_CODES[e.status]) {
    return HTTP_CODES[e.status];
  }

  // Network error
  if (typeof e.message === "string" && /fetch|network|cors/i.test(e.message)) {
    return {
      title: "Sem conexão",
      description: "Não conseguimos conectar ao servidor.",
      action: "Verifique sua internet e tente novamente.",
      severity: "warning",
    };
  }

  // Mensagem nativa como fallback
  return {
    ...GENERIC_FALLBACK,
    title: fallbackTitle ?? GENERIC_FALLBACK.title,
    description: e.message ?? GENERIC_FALLBACK.description,
  };
}

/**
 * Helper para uso em catch blocks: retorna apenas a mensagem para toast.
 */
export function friendlyMessage(err: unknown, fallback = "Erro inesperado"): string {
  const f = friendlyError(err, fallback);
  return f.action ? `${f.description} ${f.action}` : f.description;
}
